import { Suspense, useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { Loader } from '@lyearn/ui';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import dynamic from '@/utils/dynamic';

import DefaultMediaItem from '../../MediaItem/DefaultMediaItem';
import MediaListContainer from '../../MediaListContainer';
import { MediaListContainerProps } from '../../MediaListContainer/types';
import { CarouselMediaListProps } from '../types';
import { leftArrowPosition, rightArrowPosition } from './styles';

const MediaCarousel = dynamic(
  () =>
    import(
      /* webpackChunkName: "media-carousel-module" */
      '@/modules/corporate/components/Carousel'
    ),
);

const CarouselMediaList = (props: CarouselMediaListProps) => {
  const {
    medias = [],
    onRemove,
    isDownloadable,
    className,
    autoPlay,
    openMediaPlayerOnLoad,
    onMediaPlayerToggle,
    onMediaProgress,
    onClose,
  } = props;

  const { screen } = useScreen();

  const renderContent = useCallback(
    (media: EntityMediaFragment, onClick: (media: EntityMediaFragment) => void) => {
      return (
        <DefaultMediaItem
          key={media.mediaId}
          className="m-10 w-[260px]"
          media={media}
          onClick={onClick}
          onRemove={onRemove}
        />
      );
    },
    [onRemove],
  );

  const renderCarousel: MediaListContainerProps['renderList'] = useCallback(
    ({ medias, onClick }) => (
      <Suspense fallback={<Loader />}>
        <MediaCarousel
          key={screen}
          navigation
          slidesPerGroupAuto
          className="w-full"
          classes={{
            slider: className,
            leftArrowPosition,
            rightArrowPosition,
          }}
          itemWidth="auto"
          renderSlide={(index) => renderContent(medias[index], onClick)}
          slidesPerView="auto"
          spaceBetween={4}
          speed={900}
          totalCount={medias.length}
        />
      </Suspense>
    ),
    [className, renderContent, screen],
  );

  if (!medias?.length) {
    return null;
  }

  return (
    <MediaListContainer
      autoPlay={autoPlay}
      isDownloadable={isDownloadable}
      medias={medias}
      openMediaPlayerOnLoad={openMediaPlayerOnLoad}
      renderList={renderCarousel}
      onClose={onClose}
      onMediaPlayerToggle={onMediaPlayerToggle}
      onMediaProgress={onMediaProgress}
    />
  );
};

export default CarouselMediaList;
